// Small, render-free helpers shared by AssigneeSelector and its subcomponents
// (AssigneeTrigger, AssigneeMemberRow) - split out so those components don't
// each need their own copy of the hash/palette/name-shortening logic.

// Above this many candidates the dropdown gains a type-ahead search
// (all-ODO mode can list the whole company).
export const SEARCH_THRESHOLD = 8;

// Deterministic color from a name — warm, saturated palette that feels intentional
const AVATAR_COLORS = [
    '#e06c4e', // terracotta
    '#d4785c', // clay
    '#c4893a', // amber
    '#8a9a3b', // olive
    '#4a9e7e', // sage
    '#3d8fa0', // teal
    '#4e7ec2', // steel blue
    '#7367c4', // lavender
    '#a05eb5', // orchid
    '#c45a8d' // rose
];

function hashName(name: string): number {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
}

export function getAvatarColor(name: string): string {
    return AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length];
}

const HONORIFICS = new Set([
    'dr',
    'doctor',
    'mr',
    'mrs',
    'ms',
    'miss',
    'prof',
    'professor',
    'rev',
    'fr'
]);
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'dds', 'dmd', 'md', 'msd', 'phd']);

const normalize = (token: string) => token.replace(/[.,]/g, '').toLowerCase();
const isSuffixOnly = (segment: string) =>
    segment.split(/\s+/).every((token) => SUFFIXES.has(normalize(token)));

// "Williams, Sarah, DDS" → "Sarah Williams": drop suffix-only comma segments, then move the last name to the end.
function reorderLastFirst(fullname: string): string {
    const segments = fullname
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);
    const named = segments.filter((segment, i) => i === 0 || !isSuffixOnly(segment));
    return [...named.slice(1), named[0] ?? ''].join(' ');
}

// The name's words without leading honorifics or trailing suffixes (falls back to every word).
function nameTokens(fullname: string): string[] {
    const tokens = reorderLastFirst(fullname).split(/\s+/).filter(Boolean);
    const start = tokens.findIndex((token) => !HONORIFICS.has(normalize(token)));
    if (start === -1) return tokens;
    let end = tokens.length;
    while (end > start + 1 && SUFFIXES.has(normalize(tokens[end - 1]))) end -= 1;
    return tokens.slice(start, end);
}

const DOCTOR_TITLES = new Set(['dr', 'doctor']);

// Any comma segment counts: "Dr. Williams, Sarah" keeps its title too.
function hasDoctorTitle(fullname: string): boolean {
    return fullname.split(',').some((segment) => {
        const tokens = segment.trim().split(/\s+/).map(normalize);
        const end = tokens.findIndex((token) => !HONORIFICS.has(token));
        return tokens.slice(0, end === -1 ? tokens.length : end).some((t) => DOCTOR_TITLES.has(t));
    });
}

/** "Dr. Sarah Williams" → "Dr. Sarah W." | "Mr. Sam Lee" → "Sam L." | "Cher" → "Cher"; case is kept. */
export function compactName(fullname: string): string {
    const tokens = nameTokens(fullname);
    if (tokens.length === 0 || HONORIFICS.has(normalize(tokens[0]))) return tokens[0] ?? '';
    const title = hasDoctorTitle(fullname) ? 'Dr. ' : '';
    if (tokens.length < 2) return `${title}${tokens[0]}`;
    return `${title}${tokens[0]} ${tokens[tokens.length - 1][0]}.`;
}

export function getInitials(name: string): string {
    const tokens = nameTokens(name);
    const picked = tokens.length < 2 ? tokens : [tokens[0], tokens[tokens.length - 1]];
    return picked
        .map((token) => token[0])
        .join('')
        .toUpperCase();
}

// "jordan.blake@x.com" → "jordan blake": a name guess for initials when only the email is known.
export function nameFromEmail(email: string): string {
    return email
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .trim();
}
